// Products.tsx
import React, {useEffect, useState} from "react";
import { useTranslation } from 'react-i18next';
import styles from "./products.module.scss";
import ProductCard from "./products-card/products-card";
import SearchBar from "../../ui-kit/search-bar/search-bar";
import Dropdown from "../../ui-kit/dropdown/dropdown";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  fetchProductsData,
  selectFilteredTenantData,
  setSearchQuery,
  setSelectedCities,
} from "../../../services/reducers/products";
import { useSearchParams } from "react-router-dom";
import CustomScrollbar from "../../ui-kit/scrollbar/scrollbar";
import { ExportToExcel } from "../../../utils/export-to-excel";
import { removeIndustrialCity } from "../../../utils/helpers-for-cities";
import usePopStateScroll from "../../../hooks/usePopStateScroll";
import useDebounce from "../../../hooks/useDebounce";
import { EMS_BASE_URL } from "../../../utils/ems";

const Products = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const plotsData = useAppSelector((store) => store.filters.plots.data);
  const investmentData = useAppSelector(
    (store) => store.filters.investmentOpportunities.data,
  );
  const spacesData = useAppSelector(
    (store) => store.filters.spacesForRent.data,
  );
  const { loading, searchQuery, cities, selectedCities } = useAppSelector(
    (state) => state.products,
  );
  const filteredProductsData = useAppSelector(selectFilteredTenantData);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const debouncedSearch = useDebounce(localSearch, 1000);

  useEffect(() => {
    if (searchParams.has("cities") && searchParams.get("cities")) {
      const citiesFromUrl = searchParams.get("cities")!.split(",").map(item=>+item);
      dispatch(setSelectedCities(citiesFromUrl));
    } else {
      dispatch(setSelectedCities([]));
    }
  }, [searchParams, dispatch]);


  useEffect(() => {
    if (searchParams.has("search") && searchParams.get("search")) {
      const query = searchParams.get("search")!;
      setLocalSearch(query);
      dispatch(setSearchQuery(query));
    } else {
      setLocalSearch('');
      dispatch(setSearchQuery(''));
    }
  }, [searchParams, dispatch]);


  useEffect(() => {
    dispatch(setSearchQuery(debouncedSearch));
    const newParams = new URLSearchParams(searchParams);
    const currentQuery = newParams.get("search") || "";
    if (debouncedSearch.trim() !== currentQuery.trim()) {
      if (debouncedSearch) {
        newParams.set("search", debouncedSearch);
      } else {
        newParams.delete("search");
      }
      setSearchParams(newParams);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (
      plotsData.length > 0 &&
      investmentData.length > 0 &&
      spacesData.length > 0 &&
      filteredProductsData.length === 0
    ) {
      dispatch(fetchProductsData({ plotsData, investmentData, spacesData }));
    }
  }, [
    plotsData,
    investmentData,
    spacesData,
    filteredProductsData.length,
    dispatch,
  ]);

  const onSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const onCityChange = (value: number | number[] | null) => {
    if (typeof value === "number" || !value) return;
    dispatch(setSelectedCities(value));
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (value && value.length) {
        newParams.set("cities", value.join(","));
      } else {
        newParams.delete("cities");
      }
      return newParams;
    });
  };
  const exportToExcel = () => {
    let idx = 1;
    const customSheet = filteredProductsData
      .map((product) =>
        product.cities.map((city) =>
          city.companies.map((company) => ({
            "No.": idx++,
            "Estate Name": city.city,
            "Tenant Name": {
              text: `${company.name_en}\n${company.name_ar}`,
              hyperlink: `${EMS_BASE_URL}/newDir/TenantDetails.aspx?lang=&tid=${company.tenant_id}`,
            },
            "Established date": company.date_of_establish,
            "Product/Service": product.product,
            Email: company.email,
            "Mobile phone": company.mobile,
            "Other phones": [company.phone, company.other_phones]
              .filter((p) => p)
              .join(", "),
            "Website ": company.website,
          })),
        ),
      )
      .reduce((a, b) => a.concat(b))
      .reduce((a, b) => a.concat(b));

    const columns = {
      "No.": 8,
      "Estate Name": 22,
      "Tenant Name": 30,
      "Established date": 17,
      "Product/Service": 35,
      Email: 35,
      "Mobile phone": 17,
      "Other phones": 17,
      "Website ": 32,
    };
    ExportToExcel(columns, customSheet, "Products", "ProductsExport.xlsx", ["Tenant Name"]);
  };

  usePopStateScroll({ param: 'productName', prefix: 'product-closed-card-' });

  return (
    <div className={styles.products}>
      <div className={styles.staticContent}>
        <div className={styles.header}>
          <h2>{t('products.header')}</h2>
          <span className={`${styles.productsAvailable} ${(selectedCities && selectedCities.length > 0) || searchQuery ? styles.searchedResultsAvailable : ""}`}>
          {t('products.available', { count: filteredProductsData.length })}
        </span>
        </div>
        <div className={styles.filters}>
          <SearchBar
              placeholder={t('products.searchPlaceholder')}
              className={styles.search}
              searchValue={localSearch}
              onSearchChange={(e) => onSearchChange(e.currentTarget.value)}
              onClearSearch={() => onSearchChange("")}
          />
          <Dropdown
              placeholder={t('products.cityPlaceholder')}
              className={styles.dropdown}
              value={selectedCities}
              options={cities}
              listType="checkbox"
              onChange={(option) => onCityChange(option)}
              onClear={() => onCityChange([])}
              customDisplay={(value)=>removeIndustrialCity(value).cleanedName}
          />
        </div>
        <div className={styles.exportProducts}>
          <a onClick={() => exportToExcel()}>{t('products.exportLink')}</a>
        </div>
      </div>
      <CustomScrollbar marginRight='13px'>
        {!loading && (
            <div className={styles.cards}>
              {filteredProductsData.map((item: any) => (
                  <ProductCard key={item.product} name={item.product.trim()} />
              ))}
            </div>
        )}
      </CustomScrollbar>
    </div>
  );
};

export default Products;
